import React, { useState, useEffect } from "react";
import { Table, Thead, Tbody, Tr, Th, Td } from "react-super-responsive-table";
import { useSelector } from "react-redux";

import "react-super-responsive-table/dist/SuperResponsiveTableStyle.css";
import ReactPaginate from "react-paginate";
import "../../assets/css/table-mobile-responsiveness.css";
import "../../assets/css/react-confirmation-style.css";
import { jsPDF } from "jspdf";
import "jspdf-autotable";
// import { confirmAlert } from 'react-confirm-alert';
import "react-confirm-alert/src/react-confirm-alert.css";
import supabase from "../../supabaseClient";
import { fetchAllSupabaseRows } from "../../utils/supabasePagination";
import logo from "../../assets/RCS-Logo-Blue.png"; // Import the logo image
import "react-toastify/dist/ReactToastify.css";
import {
  getBillingStatusColor,
  getBillingStatusLabel,
  getShipmentStatusColor,
  isShipmentBilled,
  matchesShipmentStatusFilter,
} from "../../utils/shipmentManagementStatus";

const ShipmentManagement = () => {
  const user = useSelector((state) => state.auth.user);
  const [shipments, setShipments] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selectedShipment, setSelectedShipment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [monthFilter, setMonthFilter] = useState("");
  const [yearFilter, setYearFilter] = useState(() =>
    String(new Date().getFullYear())
  );
  const [shipmentTypeFilter, setShipmentTypeFilter] = useState("");

  const getVisibleRemarkLines = (remarkText = "") =>
    String(remarkText || "")
      .split("\n")
      .filter((line) => line && !line.startsWith("Footnote:"));

  const getVisibleRemarkText = (remarkText = "") =>
    getVisibleRemarkLines(remarkText).join("\n").trim();

  // Helper function to extract date from Job Order
  // Job Order format: [PREFIX][MONTH][COUNT][YEAR]
  // Example: BS020826 → Month 02, Year 2026
  const extractDateFromJobOrder = (jobOrder) => {
    if (!jobOrder || jobOrder.length < 8) return null;

    try {
      // Position 2-3: Month (01-12)
      const month = parseInt(jobOrder.substring(2, 4));
      // Position 6-7: Year (2-digit)
      const year = parseInt(jobOrder.substring(6, 8));

      // Validate month (1-12)
      if (month < 1 || month > 12) return null;

      // Convert 2-digit year to 4-digit (26 → 2026)
      const fullYear = 2000 + year;

      return { month, year: fullYear };
    } catch (error) {
      return null;
    }
  };
  useEffect(() => {
    const fetchAllData = async () => {
      if (!user?.id) {
        console.warn("⚠️ No user ID provided.");
        return;
      }

      setLoading(true);

      try {
        // Fetch user's department
        const { data: userInfo, error: userError } = await supabase
          .from("users")
          .select("department")
          .eq("user_id", user.id)
          .single();

        if (userError) {
          console.error("❌ Error fetching user info:", userError.message);
          return;
        }

        const userDept = userInfo?.department;
        console.log("🏢 User Department:", userDept);

        // Fetch shipments that match the department
        const { data: shipmentsData, error: shipmentError } =
          await fetchAllSupabaseRows(() =>
            supabase
              .from("shipments")
              .select("*")
              .eq("corporate_client", userDept)
          );

        if (shipmentError) {
          console.error("❌ Error fetching shipments:", shipmentError.message);
        } else {
          setShipments(shipmentsData);
        }
      } catch (err) {
        console.error("❌ Unexpected error:", err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchAllData();
  }, [user?.id]);

  const downloadShipmentPDF = () => {
    if (selectedShipment) {
      const pdf = new jsPDF({ orientation: "landscape" });
      const pageWidth = pdf.internal.pageSize.getWidth();

      // Add the logo
      const logoX = 10;
      const logoY = 10;
      const logoWidth = 40;
      const logoHeight = 20;
      pdf.addImage(logo, "PNG", logoX, logoY, logoWidth, logoHeight);

      // Title
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(20);
      pdf.text("Shipment Report", pageWidth / 2, logoY + 15, { align: "center" });
      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(11);
      pdf.text(`BL #: ${selectedShipment.tracking_no || "—"}`, pageWidth / 2, logoY + 22, {
        align: "center",
      });

      let currentY = logoY + logoHeight + 32;
      const filterValidFields = (data) =>
        data.filter(
          (row) =>
            row.details !== null &&
            row.details !== undefined &&
            row.details !== ""
        );
      const formatDateTime = (date) =>
        date
          ? new Date(date).toLocaleString("en-US", {
              month: "2-digit",
              day: "2-digit",
              year: "numeric",
              hour: "numeric",
              minute: "numeric",
              hour12: true,
              timeZone: "Asia/Manila",
            })
          : "Date not set";

      // Section: Shipper Details
      pdf.setFontSize(16);
      pdf.text("Shipper Details", 10, currentY);
      currentY += 8;

      const shipperDetails = filterValidFields([
        { description: "Shipper Name", details: selectedShipment.shipper_name },
        { description: "Shipper Unit", details: selectedShipment.shipper_unit },
        {
          description: "Shipper Email",
          details: selectedShipment.shipper_email,
        },
        { description: "Shipper City", details: selectedShipment.shipper_city },
        {
          description: "Shipper Street",
          details: selectedShipment.shipper_street,
        },
        {
          description: "Shipper Country",
          details: selectedShipment.shipper_country,
        },
        {
          description: "Shipper Phone",
          details: selectedShipment.shipper_phone,
        },
        {
          description: "Shipper Building",
          details: selectedShipment.shipper_building,
        },
      ]);

      pdf.autoTable({
        startY: currentY,
        head: [["Description", "Details"]],
        body: shipperDetails.map((row) => [row.description, row.details]),
        theme: "grid",
        styles: { fontSize: 10, cellPadding: 2 },
        headStyles: { fontStyle: "bold", fillColor: [17, 72, 124] },
        bodyStyles: { fillColor: [240, 240, 240] },
      });

      currentY = pdf.lastAutoTable.finalY + 10;

      // Section: Receiver Details
      pdf.setFontSize(16);
      pdf.text("Receiver Details", 10, currentY);
      currentY += 8;

      const receiverDetails = filterValidFields([
        {
          description: "Receiver Name",
          details: selectedShipment.receiver_name,
        },
        {
          description: "Receiver Unit",
          details: selectedShipment.receiver_unit,
        },
        {
          description: "Receiver Email",
          details: selectedShipment.receiver_email,
        },
        {
          description: "Receiver City",
          details: selectedShipment.receiver_city,
        },
        {
          description: "Receiver Street",
          details: selectedShipment.receiver_street,
        },
        {
          description: "Receiver Country",
          details: selectedShipment.receiver_country,
        },
        {
          description: "Receiver Phone",
          details: selectedShipment.receiver_phone,
        },
        {
          description: "Receiver Building",
          details: selectedShipment.receiver_building,
        },
      ]);

      pdf.autoTable({
        startY: currentY,
        head: [["Description", "Details"]],
        body: receiverDetails.map((row) => [row.description, row.details]),
        theme: "grid",
        styles: { fontSize: 10, cellPadding: 2 },
        headStyles: { fontStyle: "bold", fillColor: [17, 72, 124] },
        bodyStyles: { fillColor: [240, 240, 240] },
      });

      currentY = pdf.lastAutoTable.finalY + 10;

      // Section: Shipment Details
      pdf.setFontSize(16);
      pdf.text("Shipment Details", 10, currentY);
      currentY += 8;

      const shipmentDetails = filterValidFields([
        { description: "Shipment ID", details: selectedShipment.shipment_id },
        { description: "Job Order", details: selectedShipment.job_order },
        { description: "Type", details: selectedShipment.shipment_type },
        { description: "Origin", details: selectedShipment.shipper_city },
        { description: "Destination", details: selectedShipment.receiver_city },
        { description: "Status", details: selectedShipment.status },
        { description: "Created By", details: selectedShipment.created_by },
        {
          description: "Description",
          details: selectedShipment.shipment_description,
        },
        {
          description: "Shipment Code",
          details: selectedShipment.shipment_code,
        },

        // Additional fields from shipmentDetails
        {
          description: "Shipment Description",
          details: selectedShipment.shipment_description || null,
        },
        {
          description: "Shipment Type",
          details: selectedShipment.shipment_type || null,
        },
        {
          description: "Shipment Instructions",
          details: selectedShipment.shipment_instructions || null,
        },
        {
          description: "Shipment Mode",
          details: selectedShipment.shipment_mode || null,
        },
        {
          description: "Shipment Transport",
          details: selectedShipment.shipment_transport || null,
        },

        // Sea-specific fields
        {
          description: "Shipment Terms",
          details: selectedShipment.shipment_terms || null,
        },
        {
          description: "Shipment Packages",
          details: selectedShipment.shipment_packages || null,
        },
        {
          description: "Shipment Volume",
          details: selectedShipment.shipment_volume || null,
        },

        // Sea FCL-specific fields
        {
          description: "Shipment Containers",
          details:
            Array.isArray(selectedShipment.shipment_containers) &&
            selectedShipment.shipment_containers.length > 0
              ? selectedShipment.shipment_containers.join(", ")
              : null,
        },
        {
          description: "Shipment Weight",
          details: selectedShipment.shipment_weight || null,
        },

        // Air-specific fields
        {
          description: "Shipment Dimensions",
          details:
            Array.isArray(selectedShipment.shipment_dimensions) &&
            selectedShipment.shipment_dimensions.length > 0
              ? selectedShipment.shipment_dimensions.join(", ")
              : null,
        },
        {
          description: "Shipment Chargeable Weight",
          details: selectedShipment.shipment_chargeableweight || null,
        },

        // Sea LCL and Air-specific fields
        {
          description: "Shipment Gross Weight",
          details: selectedShipment.shipment_grossweight || null,
        },
        {
          description: "Shipment Bound",
          details: selectedShipment.shipment_bound || null,
        },
        {
          description: "Commodity",
          details: selectedShipment.commodity || null,
        },
        {
          description: "Number of Pieces",
          details: selectedShipment.number_pcs || null,
        },
        { description: "CBM", details: selectedShipment.cbm || null },
        { description: "Origin", details: selectedShipment.origin || null },
        {
          description: "Destination",
          details: selectedShipment.destination || null,
        },
        { description: "Flight", details: selectedShipment.flight || null },
        {
          description: "Truck Type",
          details: selectedShipment.truck_type || null,
        },
      ]);

      pdf.autoTable({
        startY: currentY,
        head: [["Description", "Details"]],
        body: shipmentDetails.map((row) => [row.description, row.details]),
        theme: "grid",
        styles: { fontSize: 10, cellPadding: 2 },
        headStyles: { fontStyle: "bold", fillColor: [17, 72, 124] },
        bodyStyles: { fillColor: [240, 240, 240] },
      });

      currentY = pdf.lastAutoTable.finalY + 10;

      // Section: Brokerage Remarks (if applicable)
      if (
        selectedShipment.shipment_type === "BROKERAGE" &&
        selectedShipment.brokerage_remarks?.length > 0
      ) {
        pdf.setFontSize(16);
        pdf.text("Brokerage Remarks", 10, currentY);
        currentY += 8;

        const sortedBrokerageRemarks = selectedShipment.brokerage_remarks.sort(
          (a, b) => new Date(b.timestamp) - new Date(a.timestamp)
        );

        const brokerageTableData = sortedBrokerageRemarks.map((remark) => ({
          timestamp: formatDateTime(remark.timestamp),
          title: remark.brokerage_title,
          input: remark.brokerage_input,
        }));

        pdf.autoTable({
          startY: currentY,
          head: [
            ["Date and Time", "Brokerage Remarks Title", "Brokerage Remarks"],
          ],
          body: brokerageTableData.map((row) => [
            row.timestamp,
            row.title,
            row.input,
          ]),
          theme: "grid",
          styles: { fontSize: 10, cellPadding: 3 },
          headStyles: { fontStyle: "bold", fillColor: [17, 72, 124] },
          bodyStyles: { fillColor: [240, 240, 240] },
        });

        currentY = pdf.lastAutoTable.finalY + 10;
      }

      // Section: Remarks (if applicable)
      if (selectedShipment.remarks && selectedShipment.remarks.length > 0) {
        pdf.setFontSize(16);
        pdf.text("Remarks", 10, currentY);
        currentY += 8;

        const sortedRemarks = selectedShipment.remarks.sort(
          (a, b) => new Date(b.date) - new Date(a.date)
        );

        const remarksTableData = sortedRemarks.map((remark) => {
          const receivedDate = formatDateTime(remark.receivedDate);
          const transitDate = formatDateTime(remark.transitDate);
          const aad = formatDateTime(remark.aad);
          const atd = formatDateTime(remark.atd);
          const eta = formatDateTime(selectedShipment.eta);
          const etd = formatDateTime(selectedShipment.etd);

          // Determine shipment status
          const orderPlaced = remark.status === "Order Placed";
          const departureAtOrigin = remark.status === "Departure at Origin";
          const arrivalAtDestination =
            remark.status === "Arrival at Destination";
          const inTransit = remark.status === "In Transit";

          // Construct remarks string dynamically
          let fullRemark = getVisibleRemarkText(remark.remark)
            ? `${getVisibleRemarkText(remark.remark)}\n`
            : "No Remarks Available\n";

          if (orderPlaced)
            fullRemark += `Shipment Nomination Date: ${receivedDate}\n`;
          if (orderPlaced || departureAtOrigin) fullRemark += `ETD: ${etd}\n`;
          if (departureAtOrigin) fullRemark += `ATD: ${atd}\n`;
          if (orderPlaced || arrivalAtDestination)
            fullRemark += `ETA: ${eta}\n`;
          if (arrivalAtDestination) fullRemark += `ATA: ${aad}\n`;

          // ✅ Add Transit Type & Transit Date for "In Transit" status
          if (inTransit) {
            fullRemark += `Transit Type: ${
              remark.transitType || "Date not set"
            }\n`;
            fullRemark += `Transit Date: ${transitDate}\n`;
            fullRemark += `ETD: ${etd}\n`;
            fullRemark += `ETA: ${eta}\n`;
          }
          // Debugging: Log the final remark
          // console.log("Full Remark for", remark.status, ":", fullRemark);
          return {
            dateTime: formatDateTime(remark.date),
            status: remark.status || "No Status Available",
            remarks: fullRemark.trim(), // Trim to remove extra newlines
          };
        });

        // Generate remarks table
        pdf.autoTable({
          startY: currentY,
          head: [["Date and Time", "Status", "Remarks"]],
          body: remarksTableData.map((row) => [
            row.dateTime,
            row.status,
            row.remarks,
          ]),
          theme: "grid",
          styles: { fontSize: 10, cellPadding: 3 },
          headStyles: { fontStyle: "bold", fillColor: [17, 72, 124] },
          bodyStyles: { fillColor: [240, 240, 240], cellWidth: "wrap" }, // Wrap long text
        });
      }
      // Save the PDF
      pdf.save(`shipment-${selectedShipment.tracking_no}.pdf`);
    } else {
      console.error("No shipment selected for download.");
    }
  };
  // function handleDelete(row) {
  //   confirmAlert({
  //     customUI: ({ onClose }) => (
  //       <div className="bg-white p-6 rounded-lg shadow-lg relative">
  //         <button onClick={onClose} className="absolute top-2 right-2 text-gray-500 text-[30px] hover:text-gray-700">
  //           &times;
  //         </button>
  //         <h2 className="text-lg font-semibold text-blue">Confirm Delete</h2>
  //         <p className="mt-2">Are you sure you want to delete shipment ID: {row.shipment_id}?</p>
  //         <div className="mt-4 flex justify-between">
  //           <button onClick={onClose} className="bg-gray-300 px-4 py-2 rounded">Cancel</button>
  //           <button
  //             onClick={() => {
  //               console.log(`Deleted shipment with ID: ${row.shipment_id}`);
  //               onClose();
  //             }}
  //             style={{ backgroundColor: "#dc3545", color: "white" }}
  //             className="px-4 py-2 rounded"
  //           >
  //             Delete
  //           </button>
  //         </div>
  //       </div>
  //     )
  //   });
  // }

  const filteredData = shipments
    .filter((row) => {
      // Check matching status
      const matchesStatus = matchesShipmentStatusFilter(row, statusFilter);

      // Check matching month using Job Order only
      let matchesMonth = true;
      if (monthFilter !== "") {
        const joDate = extractDateFromJobOrder(row.job_order);
        matchesMonth = joDate ? joDate.month === parseInt(monthFilter) : false;
      }

      // Check matching year using Job Order only
      let matchesYear = true;
      if (yearFilter !== "") {
        const joDate = extractDateFromJobOrder(row.job_order);
        matchesYear = joDate ? joDate.year === parseInt(yearFilter) : false;
      }

      // Check matching shipment type
      const matchesShipmentType =
        shipmentTypeFilter === ""
          ? true
          : row.shipment_type === shipmentTypeFilter;

      // Combine all filters
      return (
        matchesStatus && matchesMonth && matchesYear && matchesShipmentType
      );
    })
    .filter(
      (row) =>
        // Search term filter
        (row.shipmentId &&
          row.shipmentId.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (row.tracking_no &&
          row.tracking_no.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (row.shipper_name &&
          row.shipper_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (row.receiver_name &&
          row.receiver_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (row.shipper_city &&
          row.shipper_city.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (row.user_name &&
          row.user_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (row.receiver_city &&
          row.receiver_city.toLowerCase().includes(searchTerm.toLowerCase()))
    );
  const totalShipments = filteredData.length; // Total number of filtered shipments

  const handlePageClick = (data) => setCurrentPage(data.selected);

  const paginatedData = filteredData.slice(
    currentPage * rowsPerPage,
    (currentPage + 1) * rowsPerPage
  );

  const openModal = (shipment) => {
    setSelectedShipment(shipment);
  };

  const closeModal = () => {
    setSelectedShipment(null);
  };

  const getStatusColor = getShipmentStatusColor;
  const getBillingColor = (shipment) =>
    getBillingStatusColor(isShipmentBilled(shipment));
  const getBillingLabel = (shipment) =>
    getBillingStatusLabel(isShipmentBilled(shipment));

  const getShipmentTypeColor = (type) => {
    switch (type) {
      case 'AIR':
        return 'bg-gradient-to-r from-blue to-blue/80 text-white';
      case 'SEA':
        return 'bg-gradient-to-r from-teal-500 to-teal-600 text-white';
      case 'LAND':
        return 'bg-gradient-to-r from-green-500 to-green-600 text-white';
      case 'BROKERAGE':
        return 'bg-gradient-to-r from-purple-500 to-purple-600 text-white';
      case 'DOMESTIC':
        return 'bg-gradient-to-r from-yellow to-yellow/80 text-blue';
      default:
        return 'bg-gradient-to-r from-gray-500 to-gray-600 text-white';
    }
  };

  return (
    <section className="flex flex-col justify-center font-poppins bg-gray-100 relative overflow-auto">
      <div className="relative flex flex-col justify-center py-6 px-4">
        {/* Enhanced Header */}
        <div className="text-center mb-6">
          <div className="flex items-center justify-center gap-3 mb-3">
           
            <h1 className="text-4xl font-bold text-blue">
              Corporate Shipment Management
            </h1>
          </div>
          <p className="text-grey font-medium">
            View and manage all your department's shipments with advanced filtering
          </p>
        </div>



        {/* Modern Form Container */}
        <div className="w-full max-w-7xl mx-auto">
          <div className="rounded-3xl shadow-xl border border-white/20 overflow-hidden mb-8">
            {/* Form Header */}
            <div className="bg-blue px-8 py-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-white/20 rounded-2xl">
                  <i className="fas fa-filter text-white text-xl"></i>
                </div>
                <div>
                  <h2 className="text-white font-bold text-xl">Search & Filter</h2>
                  <p className="text-white/80 text-sm">Find and filter your shipments easily</p>
                </div>
              </div>
            </div>

            {/* Form Body */}
            <div className="p-8">
              <div className="grid grid-cols-1 lg:grid-cols-6 gap-8">
                {/* Search Section */}
                <div className="lg:col-span-6 space-y-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-8 h-8 bg-blue/10 rounded-xl flex items-center justify-center">
                      <i className="fas fa-search text-blue text-sm"></i>
                    </div>
                    <h3 className="text-blue font-bold text-lg">Search Shipments</h3>
                  </div>

                  <div>
                    <input
                      type="text"
                      placeholder="Search by ID, Tracking No, Shipper, Receiver, etc."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full p-4 border-2 border-gray-200 rounded-2xl focus:border-blue focus:ring-4 focus:ring-blue/10 transition-all duration-300 bg-white hover:border-gray-300 hover:shadow-md"
                    />
                  </div>
                </div>

                {/* Filter Sections */}
                <div className="space-y-6">
                  <div>
                    <label className="flex items-center gap-2 text-grey font-semibold mb-3 text-sm">
                      <i className="fas fa-filter text-blue text-xs"></i>
                      Filter by Status
                    </label>
                    <div className="relative">
                      <select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        className="w-full p-4 border-2 border-gray-200 rounded-2xl focus:border-blue focus:ring-4 focus:ring-blue/10 transition-all duration-300 bg-white hover:border-gray-300 hover:shadow-md appearance-none"
                      >
                        <option value="">All Status</option>
                        <option value="upcoming">Upcoming</option>
                        <option value="active">Active</option>
                        <option value="total">Total</option>
                        <option value="completed">Completed</option>
                        <option value="billed">Billed</option>
                      </select>
                      <div className="absolute inset-y-0 right-0 flex items-center pr-4 pointer-events-none">
                        <i className="fas fa-chevron-down text-blue text-sm"></i>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="space-y-6">
                  <div>
                    <label className="flex items-center gap-2 text-grey font-semibold mb-3 text-sm">
                      <i className="fas fa-calendar-alt text-blue text-xs"></i>
                      Filter by Month
                    </label>
                    <div className="relative">
                      <select
                        value={monthFilter}
                        onChange={(e) => setMonthFilter(e.target.value)}
                        className="w-full p-4 border-2 border-gray-200 rounded-2xl focus:border-blue focus:ring-4 focus:ring-blue/10 transition-all duration-300 bg-white hover:border-gray-300 hover:shadow-md appearance-none"
                      >
                        <option value="">All Months</option>
                        <option value="01">January</option>
                        <option value="02">February</option>
                        <option value="03">March</option>
                        <option value="04">April</option>
                        <option value="05">May</option>
                        <option value="06">June</option>
                        <option value="07">July</option>
                        <option value="08">August</option>
                        <option value="09">September</option>
                        <option value="10">October</option>
                        <option value="11">November</option>
                        <option value="12">December</option>
                      </select>
                      <div className="absolute inset-y-0 right-0 flex items-center pr-4 pointer-events-none">
                        <i className="fas fa-chevron-down text-blue text-sm"></i>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="space-y-6">
                  <div>
                    <label className="flex items-center gap-2 text-grey font-semibold mb-3 text-sm">
                      <i className="fas fa-calendar-check text-blue text-xs"></i>
                      Filter by Year
                    </label>
                    <div className="relative">
                      <select
                        value={yearFilter}
                        onChange={(e) => setYearFilter(e.target.value)}
                        className="w-full p-4 border-2 border-gray-200 rounded-2xl focus:border-blue focus:ring-4 focus:ring-blue/10 transition-all duration-300 bg-white hover:border-gray-300 hover:shadow-md appearance-none"
                      >
                        <option value="">All Years</option>
                        <option value="2023">2023</option>
                        <option value="2024">2024</option>
                        <option value="2025">2025</option>
                        <option value="2026">2026</option>
                        <option value="2027">2027</option>
                        <option value="2028">2028</option>
                      </select>
                      <div className="absolute inset-y-0 right-0 flex items-center pr-4 pointer-events-none">
                        <i className="fas fa-chevron-down text-blue text-sm"></i>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="space-y-6">
                  <div>
                    <label className="flex items-center gap-2 text-grey font-semibold mb-3 text-sm">
                      <i className="fas fa-truck text-blue text-xs"></i>
                      Filter by Type
                    </label>
                    <div className="relative">
                      <select
                        value={shipmentTypeFilter}
                        onChange={(e) => setShipmentTypeFilter(e.target.value)}
                        className="w-full p-4 border-2 border-gray-200 rounded-2xl focus:border-blue focus:ring-4 focus:ring-blue/10 transition-all duration-300 bg-white hover:border-gray-300 hover:shadow-md appearance-none"
                      >
                        <option value="">All Types</option>
                        <option value="AIR">Air</option>
                        <option value="SEA">Sea</option>
                        <option value="LAND">Land</option>
                        <option value="BROKERAGE">Brokerage</option>
                        <option value="DOMESTIC">Domestic</option>
                      </select>
                      <div className="absolute inset-y-0 right-0 flex items-center pr-4 pointer-events-none">
                        <i className="fas fa-chevron-down text-blue text-sm"></i>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="space-y-6">
                  <div>
                    <label className="flex items-center gap-2 text-grey font-semibold mb-3 text-sm">
                      <i className="fas fa-eye text-blue text-xs"></i>
                      Rows per Page
                    </label>
                    <div className="relative">
                      <select
                        value={rowsPerPage}
                        onChange={(e) => setRowsPerPage(Number(e.target.value))}
                        className="w-full p-4 border-2 border-gray-200 rounded-2xl focus:border-blue focus:ring-4 focus:ring-blue/10 transition-all duration-300 bg-white hover:border-gray-300 hover:shadow-md appearance-none"
                      >
                        <option value={5}>5 rows</option>
                        <option value={10}>10 rows</option>
                        <option value={25}>25 rows</option>
                        <option value={50}>50 rows</option>
                        <option value={100}>100 rows</option>
                      </select>
                      <div className="absolute inset-y-0 right-0 flex items-center pr-4 pointer-events-none">
                        <i className="fas fa-chevron-down text-blue text-sm"></i>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Modern Table/Loading Section */}
        {loading ? (
          <div className="w-full max-w-7xl mx-auto">
            <div className="rounded-3xl shadow-xl border border-white/20 overflow-hidden">
              <div className="p-12">
                <div className="flex flex-col items-center justify-center space-y-4">
                  <div className="relative">
                    <div className="w-16 h-16 border-4 border-blue/20 border-t-blue rounded-full animate-spin"></div>
                    <div className="w-16 h-16 border-4 border-transparent border-t-yellow rounded-full animate-spin absolute inset-0 animate-ping"></div>
                  </div>
                  <div className="text-blue font-medium text-lg">Loading shipments...</div>
                  <div className="text-grey text-sm">Please wait while we fetch your data</div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="w-full max-w-7xl mx-auto">
            <div className="rounded-3xl shadow-xl border border-white/20 overflow-hidden">
              {/* Table Header */}
              <div className="bg-blue px-6 py-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-white/20 rounded-xl">
                    <i className="fas fa-table text-white text-lg"></i>
                  </div>
                  <div>
                    <h3 className="text-white font-bold text-xl">Shipments Data</h3>
                    <p className="text-white/80 text-sm">
                      {paginatedData.length} of {totalShipments} shipments
                    </p>
                  </div>
                </div>
              </div>

              {/* Desktop Table View */}
              <div className="hidden lg:block overflow-x-auto">
                <Table className="w-full">
                  <Thead>
                    <Tr className="bg-gray-50 border-b border-gray-200">
                      <Th className="px-6 py-4 text-left font-semibold text-sm text-blue">
                        <div className="flex items-center gap-2">
                          <i className="fas fa-barcode text-xs"></i>
                          Way Bill No.
                        </div>
                      </Th>
                      {user?.role !== "Corporate" && (
                        <Th className="px-6 py-4 text-left font-semibold text-sm text-blue">
                          <div className="flex items-center gap-2">
                            <i className="fas fa-hashtag text-xs"></i>
                            Shipment ID
                          </div>
                        </Th>
                      )}
                      {user?.role !== "Corporate" && (
                        <Th className="px-6 py-4 text-left font-semibold text-sm text-blue">
                          <div className="flex items-center gap-2">
                            <i className="fas fa-briefcase text-xs"></i>
                            Job Order
                          </div>
                        </Th>
                      )}
                      {user?.role !== "Corporate" && (
                        <Th className="px-6 py-4 text-left font-semibold text-sm text-blue">
                          <div className="flex items-center gap-2">
                            <i className="fas fa-user text-xs"></i>
                            Created By
                          </div>
                        </Th>
                      )}
                      <Th className="px-6 py-4 text-left font-semibold text-sm text-blue">
                        <div className="flex items-center gap-2">
                          <i className="fas fa-paper-plane text-xs"></i>
                          Shipper
                        </div>
                      </Th>
                      <Th className="px-6 py-4 text-left font-semibold text-sm text-blue">
                        <div className="flex items-center gap-2">
                          <i className="fas fa-inbox text-xs"></i>
                          Receiver
                        </div>
                      </Th>
                      {user?.role !== "Corporate" && (
                        <Th className="px-6 py-4 text-left font-semibold text-sm text-blue">
                          <div className="flex items-center gap-2">
                            <i className="fas fa-truck text-xs"></i>
                            Type
                          </div>
                        </Th>
                      )}
                      {user?.role !== "Corporate" && (
                        <Th className="px-6 py-4 text-left font-semibold text-sm text-blue">
                          <div className="flex items-center gap-2">
                            <i className="fas fa-map-marker-alt text-xs"></i>
                            Origin
                          </div>
                        </Th>
                      )}
                      {user?.role !== "Corporate" && (
                        <Th className="px-6 py-4 text-left font-semibold text-sm text-blue">
                          <div className="flex items-center gap-2">
                            <i className="fas fa-flag-checkered text-xs"></i>
                            Destination
                          </div>
                        </Th>
                      )}
                      <Th className="px-6 py-4 text-left font-semibold text-sm text-blue">
                        <div className="flex items-center gap-2">
                          <i className="fas fa-info-circle text-xs"></i>
                          Status
                        </div>
                      </Th>
                      <Th className="px-6 py-4 text-left font-semibold text-sm text-blue">
                        <div className="flex items-center gap-2">
                          <i className="fas fa-comment text-xs"></i>
                          Latest Remark
                        </div>
                      </Th>
                      <Th className="px-6 py-4 text-center font-semibold text-sm text-blue bg-gray-100 sticky right-0">
                        <div className="flex items-center justify-center gap-2">
                          <i className="fas fa-cog text-xs"></i>
                          Actions
                        </div>
                      </Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {paginatedData.length > 0 ? (
                      paginatedData.map((row, index) => (
                        <Tr key={index} className="hover:bg-blue/5 transition-all duration-200 border-b border-gray-100 group">
                          <Td className="px-6 py-4 bg-blue/5 font-mono text-sm font-semibold text-blue">
                            {row.tracking_no}
                          </Td>
                          {user?.role !== "Corporate" && (
                            <Td className="px-6 py-4 font-medium text-grey">
                              {row.shipment_id}
                            </Td>
                          )}
                          {user?.role !== "Corporate" && (
                            <Td className="px-6 py-4 text-grey">
                              {row.job_order}
                            </Td>
                          )}
                          {user?.role !== "Corporate" && (
                            <Td className="px-6 py-4">
                              <div className="flex items-center gap-2">
                                <div className="w-8 h-8 bg-gradient-to-br from-blue/20 to-blue/30 rounded-full flex items-center justify-center">
                                  <i className="fas fa-user text-blue text-xs"></i>
                                </div>
                                <span className="font-medium text-grey">{row.created_by}</span>
                              </div>
                            </Td>
                          )}
                          <Td className="px-6 py-4 font-medium text-grey">
                            {row.shipper_name}
                          </Td>
                          <Td className="px-6 py-4 font-medium text-grey">
                            {row.receiver_name}
                          </Td>
                          {user?.role !== "Corporate" && (
                            <Td className="px-6 py-4">
                              <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold shadow-sm ${getShipmentTypeColor(row.shipment_type)}`}>
                                <div className="w-1.5 h-1.5 bg-current rounded-full mr-2"></div>
                                {row.shipment_type}
                              </span>
                            </Td>
                          )}
                          {user?.role !== "Corporate" && (
                            <Td className="px-6 py-4 font-medium text-grey">
                              {row.shipper_city}
                            </Td>
                          )}
                          {user?.role !== "Corporate" && (
                            <Td className="px-6 py-4 font-medium text-grey">
                              {row.receiver_city}
                            </Td>
                          )}
                          <Td className="px-6 py-4">
                            <div className="flex flex-col items-start gap-2">
                              <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(row.status)}`}>
                                <div className="w-1.5 h-1.5 bg-current rounded-full mr-2 animate-pulse"></div>
                                {row.status}
                              </span>
                              <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${getBillingColor(row)}`}>
                                <i className={`fas ${isShipmentBilled(row) ? 'fa-check-circle' : 'fa-times-circle'} mr-2`}></i>
                                {getBillingLabel(row)}
                              </span>
                            </div>
                          </Td>
                          <Td className="px-6 py-4 max-w-xs">
                            {row.remarks && row.remarks.length > 0 ? (
                              (() => {
                                const latestRemark = row.remarks.reduce((latest, current) =>
                                  new Date(latest.date) > new Date(current.date) ? latest : current
                                );
                                return (
                                  <div className="text-sm">
                                    <div className="font-medium text-grey mb-1">
                                      {new Date(latestRemark.date).toLocaleDateString()}
                                    </div>
                                    <div className="text-grey/70 truncate">
                                      {getVisibleRemarkText(latestRemark.remark) || "No remark available"}
                                    </div>
                                  </div>
                                );
                              })()
                            ) : (
                              <span className="text-grey/50 text-sm">No remarks</span>
                            )}
                          </Td>
                          <Td className="px-6 py-4 bg-gray-200 sticky right-0">
                            <button
                              onClick={() => openModal(row)}
                              className="group bg-blue hover:from-blue/90 hover:to-blue text-white px-4 py-2 rounded-xl font-medium transition-all duration-300 transform hover:scale-105 shadow-md hover:shadow-lg flex items-center gap-2"
                            >
                              <i className="fas fa-eye text-sm group-hover:animate-pulse"></i>
                              View
                            </button>
                          </Td>
                        </Tr>
                      ))
                    ) : (
                      <Tr>
                        <Td colSpan={user?.role !== "Corporate" ? 12 : 6} className="text-center py-12">
                          <div className="flex flex-col items-center gap-4">
                            <div className="w-16 h-16 bg-blue/10 rounded-full flex items-center justify-center">
                              <i className="fas fa-search text-blue text-xl"></i>
                            </div>
                            <div>
                              <h3 className="text-lg font-semibold text-blue mb-2">No Shipments Found</h3>
                              <p className="text-grey">Try adjusting your search or filter criteria</p>
                            </div>
                          </div>
                        </Td>
                      </Tr>
                    )}
                  </Tbody>
                </Table>
              </div>

              {/* Mobile Card View */}
              <div className="lg:hidden p-4 space-y-4">
                {paginatedData.length > 0 ? (
                  paginatedData.map((row, index) => (
                    <div 
                      key={index} 
                      className="bg-white rounded-2xl p-5 shadow-md border border-gray-100 hover:shadow-xl transition-all duration-300"
                    >
                      {/* Header Row */}
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-12 h-12 bg-blue rounded-xl flex items-center justify-center shadow-md flex-shrink-0">
                          <i className="fas fa-shipping-fast text-white text-lg"></i>
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-bold text-blue text-lg truncate">
                            {row.tracking_no}
                          </h3>
                          {user?.role !== "Corporate" && (
                            <p className="text-sm text-grey">ID: {row.shipment_id}</p>
                          )}
                        </div>
                      </div>

                      {/* Status Badge */}
                      <div className="flex flex-wrap gap-2 mb-4">
                        <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(row.status)}`}>
                          <div className="w-1.5 h-1.5 bg-current rounded-full mr-2 animate-pulse"></div>
                          {row.status}
                        </span>
                        <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${getBillingColor(row)}`}>
                          <i className={`fas ${isShipmentBilled(row) ? 'fa-check-circle' : 'fa-times-circle'} mr-2`}></i>
                          {getBillingLabel(row)}
                        </span>
                        {user?.role !== "Corporate" && (
                          <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${getShipmentTypeColor(row.shipment_type)}`}>
                            <div className="w-1.5 h-1.5 bg-current rounded-full mr-2"></div>
                            {row.shipment_type}
                          </span>
                        )}
                      </div>
                      
                      {/* Info Grid */}
                      <div className="space-y-3 mb-4">
                        {user?.role !== "Corporate" && (
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 bg-blue/10 rounded-xl flex items-center justify-center flex-shrink-0">
                              <i className="fas fa-user text-blue text-sm"></i>
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-medium text-grey truncate">
                                {row.created_by}
                              </div>
                              <div className="text-xs text-grey/70">Created By</div>
                            </div>
                          </div>
                        )}
                        
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-green-100 rounded-xl flex items-center justify-center flex-shrink-0">
                            <i className="fas fa-users text-green-600 text-sm"></i>
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium text-grey truncate">
                              {row.shipper_name} → {row.receiver_name}
                            </div>
                            <div className="text-xs text-grey/70">Shipper → Receiver</div>
                          </div>
                        </div>

                        {user?.role !== "Corporate" && (
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 bg-purple-100 rounded-xl flex items-center justify-center flex-shrink-0">
                              <i className="fas fa-route text-purple-600 text-sm"></i>
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-medium text-grey">
                                {row.shipper_city} → {row.receiver_city}
                              </div>
                              <div className="text-xs text-grey/70">Route</div>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Latest Remark */}
                      {row.remarks && row.remarks.length > 0 && (
                        <div className="bg-gray-50 rounded-xl p-3 mb-4">
                          {(() => {
                            const latestRemark = row.remarks.reduce((latest, current) =>
                              new Date(latest.date) > new Date(current.date) ? latest : current
                            );
                            return (
                              <div>
                                <div className="text-xs text-grey/70 mb-1">Latest Remark</div>
                                <div className="text-sm font-medium text-grey mb-1">
                                  {new Date(latestRemark.date).toLocaleDateString()}
                                </div>
                                <div className="text-sm text-grey/80 line-clamp-2">
                                  {getVisibleRemarkText(latestRemark.remark) || "No remark available"}
                                </div>
                              </div>
                            );
                          })()}
                        </div>
                      )}

                      {/* Action Button */}
                      <button
                        onClick={() => openModal(row)}
                        className="w-full bg-blue hover:from-blue/90 hover:to-blue text-white py-3 rounded-xl font-medium transition-all duration-300 transform hover:scale-[1.02] shadow-md hover:shadow-lg flex items-center justify-center gap-2"
                      >
                        <i className="fas fa-eye"></i>
                        View Details
                      </button>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-12">
                    <div className="w-16 h-16 bg-blue/10 rounded-full flex items-center justify-center mx-auto mb-4">
                      <i className="fas fa-search text-blue text-xl"></i>
                    </div>
                    <h3 className="text-lg font-semibold text-blue mb-2">No Shipments Found</h3>
                    <p className="text-grey">Try adjusting your search or filter criteria</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Enhanced Pagination */}
        {Math.ceil(filteredData.length / rowsPerPage) > 1 && (
          <div className="w-full max-w-7xl mx-auto mt-6">
            <div className="rounded-3xl shadow-xl border border-white/20 overflow-hidden">
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4 p-6">
                <div className="text-sm text-grey font-medium text-center sm:text-left">
                  Showing <span className="font-semibold text-blue">{currentPage * rowsPerPage + 1}-{Math.min((currentPage + 1) * rowsPerPage, filteredData.length)}</span> of <span className="font-semibold text-blue">{filteredData.length}</span> shipments
                </div>
                
                <div className="w-full sm:w-auto">
                  <ReactPaginate
                    previousLabel={
                      <div className="flex items-center gap-2">
                        <i className="fas fa-chevron-left text-xs"></i>
                        <span className="hidden xs:inline text-sm">Previous</span>
                      </div>
                    }
                    nextLabel={
                      <div className="flex items-center gap-2">
                        <span className="hidden xs:inline text-sm">Next</span>
                        <i className="fas fa-chevron-right text-xs"></i>
                      </div>
                    }
                    breakLabel={"..."}
                    pageCount={Math.ceil(filteredData.length / rowsPerPage)}
                    marginPagesDisplayed={1}
                    pageRangeDisplayed={2}
                    onPageChange={handlePageClick}
                    containerClassName="flex items-center justify-center gap-2"
                    pageClassName="flex-shrink-0"
                    pageLinkClassName="w-10 h-10 rounded-xl border border-gray-200 hover:border-blue/30 flex items-center justify-center text-sm font-medium text-grey hover:text-blue hover:bg-blue/5 transition-all duration-200"
                    activeClassName="!bg-gradient-to-br !from-blue !to-blue/90 !border-blue !rounded-xl shadow-md"
                    activeLinkClassName="!text-white font-semibold hover:!text-white"
                    previousClassName="flex-shrink-0 px-4 py-2 rounded-xl border border-gray-200 hover:border-blue/30 hover:bg-blue/5 transition-all duration-200"
                    previousLinkClassName="flex items-center gap-2 text-sm font-medium text-grey hover:text-blue"
                    nextClassName="flex-shrink-0 px-4 py-2 rounded-xl border border-gray-200 hover:border-blue/30 hover:bg-blue/5 transition-all duration-200"
                    nextLinkClassName="flex items-center gap-2 text-sm font-medium text-grey hover:text-blue"
                    disabledClassName="opacity-50 cursor-not-allowed hover:bg-transparent hover:border-gray-200 hover:text-grey"
                    breakClassName="px-2 py-2 text-grey text-sm"
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Modern Modal */}
        {selectedShipment && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-white/95 backdrop-blur-xl w-full max-w-2xl rounded-3xl shadow-2xl border border-white/20 overflow-hidden animate-in fade-in-0 zoom-in-95 duration-300">
              {/* Modal Header */}
              <div className="bg-blue px-8 py-6 relative">
                <button 
                  onClick={closeModal} 
                  className="absolute top-4 right-4 w-10 h-10 bg-white/20 hover:bg-white/30 rounded-xl flex items-center justify-center text-white hover:text-white transition-all duration-200"
                >
                  <i className="fas fa-times text-lg"></i>
                </button>
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-white/20 rounded-2xl">
                    <i className="fas fa-eye text-white text-2xl"></i>
                  </div>
                  <div>
                    <h2 className="text-white font-bold text-2xl">Shipment Details</h2>
                    <p className="text-white/80">View comprehensive shipment information</p>
                  </div>
                </div>
              </div>

              {/* Modal Body */}
              <div className="p-8 max-h-96 overflow-y-auto">
                <div className="space-y-6">
                  {/* Main Info Grid */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-4">
                      <div className="flex flex-col p-4 bg-blue/5 rounded-2xl">
                        <span className="text-grey font-medium mb-2">Tracking Number</span>
                        <span className="text-blue font-bold text-lg font-mono break-all">{selectedShipment.tracking_no}</span>
                      </div>
                      
                      <div className="flex flex-col p-4 bg-gray-50 rounded-2xl">
                        <span className="text-grey font-medium mb-2">Client Name</span>
                        <span className="text-blue font-bold break-words">{selectedShipment.shipper_name}</span>
                      </div>

                      <div className="flex flex-col p-4 bg-blue/5 rounded-2xl">
                        <span className="text-grey font-medium mb-2">Shipment Type</span>
                        <span className={`px-3 py-1 rounded-full text-sm font-semibold w-fit ${getShipmentTypeColor(selectedShipment.shipment_type)}`}>
                          {selectedShipment.shipment_type}
                        </span>
                      </div>
                    </div>

                    <div className="space-y-4">
                      <div className="flex flex-col p-4 bg-gray-50 rounded-2xl">
                        <span className="text-grey font-medium mb-2">Receiver</span>
                        <span className="text-blue font-bold break-words">{selectedShipment.receiver_name}</span>
                      </div>

                      <div className="flex flex-col p-4 bg-blue/5 rounded-2xl">
                        <span className="text-grey font-medium mb-2">Status</span>
                        <span className={`px-3 py-1 rounded-full text-sm font-medium w-fit ${getStatusColor(selectedShipment.status)}`}>
                          <div className="w-1.5 h-1.5 bg-current rounded-full mr-2 animate-pulse inline-block"></div>
                          {selectedShipment.status}
                        </span>
                      </div>

                      <div className="flex flex-col p-4 bg-gray-50 rounded-2xl">
                        <span className="text-grey font-medium mb-2">Billing Status</span>
                        <span className={`px-3 py-1 rounded-full text-sm font-medium w-fit ${getBillingColor(selectedShipment)}`}>
                          <i className={`fas ${isShipmentBilled(selectedShipment) ? 'fa-check-circle' : 'fa-times-circle'} mr-2`}></i>
                          {getBillingLabel(selectedShipment)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Modal Footer */}
              <div className="bg-gray-50 px-8 py-6 flex flex-col sm:flex-row gap-4 justify-between">
                <button 
                  onClick={closeModal} 
                  className="px-6 py-3 bg-gray-200 hover:bg-gray-300 text-grey font-medium rounded-2xl transition-all duration-200 hover:shadow-md"
                >
                  <i className="fas fa-arrow-left mr-2"></i>
                  Go Back
                </button>
                <button 
                  onClick={downloadShipmentPDF} 
                  className="px-6 py-3 bg-blue hover:from-blue/90 hover:to-blue text-white font-medium rounded-2xl transition-all duration-300 transform hover:scale-105 shadow-md hover:shadow-lg"
                >
                  <i className="fas fa-download mr-2"></i>
                  Download PDF
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      
    </section>
  );
};

export default ShipmentManagement;
